import express from "express";
import { createServer as createHTTPServer } from "http";
import { createReadStream, unlinkSync, existsSync, writeFileSync } from "fs";
import { stat, unlink } from "fs/promises";
import { randomUUID, randomBytes } from "node:crypto";
import { hostname as getHostname } from "node:os";
import path from "node:path";
import { StreamableHTTPServerTransport } from "@modelcontextprotocol/sdk/server/streamableHttp.js";
import { isInitializeRequest } from "@modelcontextprotocol/sdk/types.js";
import type { Logger } from "pino";
import { createBranchChangeRouteHandler } from "./script-route-branch-handler.js";
import { seedMcpVault } from "./mcp-vault/seed.js";
import { seedGlobalRoles } from "./roles/seed.js";
import {
  startClaudeUsageProbe,
  stopClaudeUsageProbe,
} from "./agent/providers/claude/usage-probe.js";

export type ListenTarget =
  | { type: "tcp"; host: string; port: number }
  | { type: "socket"; path: string }
  | { type: "pipe"; path: string };

function resolveBoundListenTarget(
  listenTarget: ListenTarget,
  httpServer: ReturnType<typeof createHTTPServer>,
): ListenTarget {
  if (listenTarget.type !== "tcp") {
    return listenTarget;
  }

  const address = httpServer.address();
  if (!address || typeof address === "string") {
    throw new Error("HTTP server did not expose a TCP address after listening");
  }

  return {
    type: "tcp",
    host: listenTarget.host,
    port: address.port,
  };
}

// Matches a Windows drive-letter path like C:\ or D:\
const WINDOWS_DRIVE_RE = /^[A-Za-z]:\\/;

export function parseListenString(listen: string): ListenTarget {
  // 1. Windows named pipes: \\.\pipe\... or pipe://...
  if (listen.startsWith("\\\\.\\pipe\\") || listen.startsWith("pipe://")) {
    return {
      type: "pipe",
      path: listen.startsWith("pipe://") ? listen.slice("pipe://".length) : listen,
    };
  }
  // 2. Explicit unix:// prefix
  if (listen.startsWith("unix://")) {
    return { type: "socket", path: listen.slice(7) };
  }
  // 3. Reject Windows absolute drive paths — they are not Unix sockets
  if (WINDOWS_DRIVE_RE.test(listen)) {
    throw new Error(`Invalid listen string (Windows path is not a valid listen target): ${listen}`);
  }
  // 4. POSIX absolute path (/ or ~) — Unix socket
  if (listen.startsWith("/") || listen.startsWith("~")) {
    return { type: "socket", path: listen };
  }
  // 5. Pure numeric — TCP port on 127.0.0.1
  const trimmed = listen.trim();
  if (/^\d+$/.test(trimmed)) {
    const port = parseInt(trimmed, 10);
    return { type: "tcp", host: "127.0.0.1", port };
  }
  // 6. host:port — TCP
  if (listen.includes(":")) {
    const [host, portStr] = listen.split(":");
    const parsedPort = parseInt(portStr, 10);
    if (!Number.isFinite(parsedPort)) {
      throw new Error(`Invalid port in listen string: ${listen}`);
    }
    return { type: "tcp", host: host || "127.0.0.1", port: parsedPort };
  }
  throw new Error(`Invalid listen string: ${listen}`);
}

function formatListenTarget(listenTarget: ListenTarget | null): string | null {
  if (!listenTarget) {
    return null;
  }
  if (listenTarget.type === "tcp") {
    return `${listenTarget.host}:${listenTarget.port}`;
  }
  return listenTarget.path;
}

import { VoiceAssistantWebSocketServer } from "./websocket-server.js";
import { createGitHubService } from "../services/github-service.js";
import { createAppostleWorktree } from "./appostle-worktree-service.js";
import { createWorktreeCoreDeps } from "./worktree-core.js";
import { DownloadTokenStore } from "./file-download/token-store.js";
import type { OpenAiSpeechProviderConfig } from "./speech/providers/openai/config.js";
import type { LocalSpeechProviderConfig } from "./speech/providers/local/config.js";
import type { RequestedSpeechProviders } from "./speech/speech-types.js";
import { createSpeechService } from "./speech/speech-runtime.js";
import { AgentManager } from "./agent/agent-manager.js";
import { ClaudeTokenStore } from "./claude-token-store.js";
import { AgentStorage } from "./agent/agent-storage.js";
import { attachAgentStoragePersistence } from "./persistence-hooks.js";
import { createAgentMcpServer } from "./agent/mcp-server.js";
import {
  createAppostleHttpMcpServer,
  type CrossHostExecOptions,
} from "./agent/appostle-http-mcp-server.js";
import { buildAppostleRuntimeMachineContext } from "./agent/appostle-system-prompt.js";
import {
  buildProviderRegistry,
  createAllClients,
  shutdownProviders,
} from "./agent/provider-registry.js";
import { ProviderSnapshotManager } from "./agent/provider-snapshot-manager.js";
import { bootstrapWorkspaceRegistries } from "./workspace-registry-bootstrap.js";
import { FileBackedProjectRegistry, FileBackedWorkspaceRegistry } from "./workspace-registry.js";
import { WorkspaceStacksService } from "./workspace-stacks-service.js";
import { NoRemoteError, TodoStore } from "./todo-store.js";
import { GitRemoteResolver, resolveCanonicalProjectId } from "./git-remote-resolver.js";
import { FileBackedChatService } from "./chat/chat-service.js";
import { CheckoutDiffManager } from "./checkout-diff-manager.js";
import { LoopService } from "./loop-service.js";
import { ScheduleService } from "./schedule/service.js";
import { TodoSchedulerService } from "./todo-scheduler.js";
import { QuestService } from "./quest/service.js";
import { LoopRunner } from "./quest/runner-loop.js";
import { OrchestratorRunner } from "./quest/runner-orchestrator.js";
import { RalphRunner } from "./quest/runner-ralph.js";
import { DaemonConfigStore } from "./daemon-config-store.js";
import { WorkspaceGitServiceImpl } from "./workspace-git-service.js";
import { createTerminalManager, type TerminalManager } from "../terminal/terminal-manager.js";
import {
  buildDirectEndpointCandidates,
  createConnectionOfferV2,
  encodeOfferToFragmentUrl,
} from "./connection-offer.js";
import { enrollDaemonClientIdentity } from "./cross-host/enroll-client-identity.js";
import { attachLocalTcpForwardListener } from "./cross-host/local-tcp-forward-listener.js";
import { createAuthServerLookup } from "./cross-host/relay-dialer.js";
import { loadOrCreateDaemonClientIdentity } from "./daemon-client-identity.js";
import { loadOrCreateDaemonKeyPair } from "./daemon-keypair.js";
import { startRelayTransport, type RelayTransportController } from "./relay-transport.js";
import {
  type AuthServerClient,
  type AuthServerSessionClient,
  type AuthServerTodoClient,
  createAuthServerClient,
  createAuthServerSessionClient,
  createAuthServerTodoClient,
} from "./auth-server-client.js";
import { loadPersistedConfig } from "./persisted-config.js";
import { stampLegacyOwnerIds } from "./migrations/stamp-legacy-owners.js";
import {
  archiveAgentTabsAcrossWatchers,
  createOwnAgentTabInheritingStack,
  createSharedSessionTab,
  deleteProjectOnAuthServer,
  deleteSharedSessionTab,
  runTabBackfill,
  runWorkspaceBackfill,
} from "./tab-backfill.js";
import { getOrCreateServerId } from "./server-id.js";
import { resolveDaemonVersion } from "./daemon-version.js";
import type { AgentClient, AgentProvider } from "./agent/agent-sdk-types.js";
import type {
  AgentProviderRuntimeSettingsMap,
  ProviderOverride,
} from "./agent/provider-launch-config.js";
import {
  ScriptRouteStore,
  createScriptProxyMiddleware,
  createScriptProxyUpgradeHandler,
} from "./script-proxy.js";
import { ScriptHealthMonitor } from "./script-health-monitor.js";
import { createScriptStatusEmitter } from "./script-status-projection.js";
import { WorkspaceScriptRuntimeStore } from "./workspace-script-runtime-store.js";
import { isHostnameAllowed, type HostnamesConfig } from "./hostnames.js";

type AgentMcpTransportMap = Map<string, StreamableHTTPServerTransport>;

function formatHostForHttpUrl(host: string): string {
  return host.includes(":") && !host.startsWith("[") ? `[${host}]` : host;
}

function createAgentMcpBaseUrl(listenTarget: ListenTarget | null): string | null {
  if (!listenTarget || listenTarget.type !== "tcp") {
    return null;
  }
  return new URL(
    "/mcp/agents",
    `http://${formatHostForHttpUrl(listenTarget.host)}:${listenTarget.port}`,
  ).toString();
}

function createAppostleMcpBaseUrl(listenTarget: ListenTarget | null): string | null {
  if (!listenTarget || listenTarget.type !== "tcp") {
    return null;
  }
  return new URL(
    "/mcp/appostle",
    `http://${formatHostForHttpUrl(listenTarget.host)}:${listenTarget.port}`,
  ).toString();
}

export type AppostleOpenAIConfig = OpenAiSpeechProviderConfig;
export type AppostleLocalSpeechConfig = LocalSpeechProviderConfig;

export type AppostleSpeechConfig = {
  providers: RequestedSpeechProviders;
  local?: AppostleLocalSpeechConfig;
};

export type DaemonLifecycleIntent =
  | {
      type: "shutdown";
      clientId: string;
      requestId: string;
    }
  | {
      type: "restart";
      clientId: string;
      requestId: string;
      reason?: string;
    };

export type AppostleDaemonConfig = {
  listen: string;
  appostleHome: string;
  corsAllowedOrigins: string[];
  allowedHosts?: HostnamesConfig;
  hostnames?: HostnamesConfig;
  mcpEnabled?: boolean;
  mcpInjectIntoAgents?: boolean;
  /** Pass --chrome to Claude agents so browser MCP tools are available. Defaults to true. */
  chromeEnabled?: boolean;
  /** When false, Playwright MCP server is not injected into agents. Defaults to true. */
  playwrightEnabled?: boolean;
  /**
   * When true, a guest (non-owner) spawning a Claude agent must have added their
   * own `claude setup-token`; otherwise the spawn is rejected. When false (the
   * pre-launch default), the guard is parked and tokenless guests fall back to
   * the owner's Claude account. Env: `APPOSTLE_REQUIRE_GUEST_CLAUDE_TOKEN`.
   */
  requireGuestClaudeToken?: boolean;
  /** Lucide icon id set by the operator, shown in host switchers. */
  daemonIcon?: string;
  staticDir: string;
  mcpDebug: boolean;
  agentClients: Partial<Record<AgentProvider, AgentClient>>;
  agentStoragePath: string;
  relayEnabled?: boolean;
  relayEndpoint?: string;
  relayPublicEndpoint?: string;
  appBaseUrl?: string;
  openai?: AppostleOpenAIConfig;
  speech?: AppostleSpeechConfig;
  voiceLlmProvider?: AgentProvider | null;
  voiceLlmProviderExplicit?: boolean;
  voiceLlmModel?: string | null;
  dictationFinalTimeoutMs?: number;
  downloadTokenTtlMs?: number;
  agentProviderSettings?: AgentProviderRuntimeSettingsMap;
  providerOverrides?: Record<string, ProviderOverride>;
  onLifecycleIntent?: (intent: DaemonLifecycleIntent) => void;
};

export interface AppostleDaemon {
  config: AppostleDaemonConfig;
  agentManager: AgentManager;
  agentStorage: AgentStorage;
  terminalManager: TerminalManager;
  scriptRouteStore: ScriptRouteStore;
  scriptRuntimeStore: WorkspaceScriptRuntimeStore;
  start(): Promise<void>;
  stop(): Promise<void>;
  getListenTarget(): ListenTarget | null;
}

export async function createAppostleDaemon(
  config: AppostleDaemonConfig,
  rootLogger: Logger,
): Promise<AppostleDaemon> {
  const logger = rootLogger.child({ module: "bootstrap" });
  const bootstrapStart = performance.now();
  const elapsed = () => `${(performance.now() - bootstrapStart).toFixed(0)}ms`;
  const daemonVersion = resolveDaemonVersion(import.meta.url);
  const daemonConfigStore = new DaemonConfigStore(
    config.appostleHome,
    {
      mcp: { injectIntoAgents: config.mcpInjectIntoAgents ?? true },
      identity: config.daemonIcon ? { icon: config.daemonIcon } : {},
    },
    logger,
  );

  try {
    const serverId = getOrCreateServerId(config.appostleHome, { logger });
    const daemonKeyPair = await loadOrCreateDaemonKeyPair(config.appostleHome, logger);
    // Cross-host SSH dialer identity. Separate from the server-role
    // `daemonKeyPair` above — this is the keypair this daemon uses when it
    // acts as a relay CLIENT of another sibling daemon (cross-host SSH). The
    // public half is enrolled with the auth-server as a `devices` row under
    // the host owner so sibling daemons' allow-lists resolve it; see
    // `daemon-client-identity.ts` for the rotation rationale.
    const daemonClientIdentity = await loadOrCreateDaemonClientIdentity(
      config.appostleHome,
      logger,
    );
    // Refresh Appostle's bundled global roles into `<appostleHome>/.roles/`.
    // Always overwrites the seeded files so customers stay on the latest
    // prompts; user-authored roles outside the seeded paths are left
    // untouched. See `./roles/seed.ts` for the full contract.
    await seedGlobalRoles(config.appostleHome, { logger });
    // Creation-only for bundled templates, always-apply for the vault: land any
    // newly shipped MCP server definitions into `<appostleHome>/mcp-servers/`,
    // then compile the current vault into the user's installed providers
    // (Claude / OpenCode / Codex). This keeps a synced Dropbox hub converged
    // without clobbering already-seeded vault files. See `./mcp-vault/seed.ts`.
    await seedMcpVault(config.appostleHome, { logger });
    let relayTransport: RelayTransportController | null = null;
    // Account-bound pairing (regime B) is the only supported model. The daemon
    // refuses to start unless it has already been paired to an auth-server
    // account — i.e. `~/.appostle/config.json` contains a non-null `account`
    // block written by `appostle link` / `appostle onboard` / `appostle login`.
    //
    // The previous behaviour silently fell back to "single-tenant standalone
    // mode" (every session had `ownerUserId: null`, ACLs no-op'd, every user
    // saw every agent). On 2026-05-18 that fallback caused production cross-
    // user bleed during the window between daemon restart and the first
    // link_account_request. The fallback is gone.
    // Daemon boot mode is decided here. The previous code threw when there
    // was no `account` block, which put the daemon into a systemd restart
    // loop on fresh installs — and there was no way out, because the WS
    // surface (which carries `link_account_request`) was never reachable.
    //
    // Fix 2: split into two modes.
    //   - "linked"         — `persisted.account` present → full boot
    //                        (auth-server client, allow-list poll, agent
    //                        managers, schedule/loop/quest services).
    //   - "awaiting_link"  — `persisted.account` absent → MINIMAL boot.
    //                        HTTP + WS + relay come up, but nothing else.
    //                        Every WS message except `link_account_request`
    //                        is rejected with `daemon_awaiting_link_response`.
    //                        On successful link the daemon `process.exit(0)`s
    //                        and systemd restarts it — the second boot
    //                        takes the "linked" branch. We rely on the
    //                        installer setting `Restart=always` (Fix 2,
    //                        installer/src/daemon-setup.ts) for the exit
    //                        to trigger an auto-restart.
    type DaemonMode = "linked" | "awaiting_link";
    const daemonMode: DaemonMode = loadPersistedConfig(config.appostleHome, logger).account
      ? "linked"
      : "awaiting_link";
    logger.info({ mode: daemonMode }, `daemon mode: ${daemonMode}`);

    // Hoisted above `createAuthServerClient` so its `directEndpoints`
    // callback can safely close over it. The actual `httpServer.listen()`
    // assignment happens later in `start()`; before then this stays null
    // and the callback returns []. Previous bug: declared at the bottom of
    // this scope, causing a TDZ ReferenceError the first time the
    // allowlist poll fired during startup.
    let boundListenTarget: ListenTarget | null = null;

    let authServerClient: AuthServerClient | null = null;
    let authServerTodoClient: AuthServerTodoClient | null = null;
    // Auth-server session-projection client. Stays null in `awaiting_link`
    // mode (no daemon token yet); created alongside the other auth-server
    // clients in the `linked` branch below. Passed into AgentManager so
    // every emitState fans out a debounced session upsert.
    let authServerSessionClient: AuthServerSessionClient | null = null;
    let ownerUserId: string | null = null;
    let linkedServerPubkeyHex: string | null = null;
    let linkedDaemonAccount: {
      authServerUrl: string;
      daemonToken: string;
      hostId: string;
    } | null = null;
    if (daemonMode === "linked") {
      const persisted = loadPersistedConfig(config.appostleHome, logger);
      // Re-read so TS narrows `persisted.account` to non-null below; the
      // first load above is intentionally throwaway just for the mode check.
      const account = persisted.account!;
      ownerUserId = account.userId;
      linkedServerPubkeyHex = account.serverPubkey;
      linkedDaemonAccount = {
        authServerUrl: account.authServerUrl,
        daemonToken: account.daemonToken,
        hostId: account.hostId,
      };
      authServerClient = createAuthServerClient({
        logger,
        authServerUrl: account.authServerUrl,
        daemonToken: account.daemonToken,
        serverPubkeyHex: account.serverPubkey,
        directEndpoints: () =>
          boundListenTarget?.type === "tcp"
            ? buildDirectEndpointCandidates({
                listenHost: boundListenTarget.host,
                port: boundListenTarget.port,
              })
            : [],
        onUserRemoved: (userId, reason) => {
          wsServer?.closeSessionsForUser(userId, `auth-allowlist:${reason}`);
        },
      });
      authServerTodoClient = createAuthServerTodoClient({
        logger,
        authServerUrl: account.authServerUrl,
        daemonToken: account.daemonToken,
      });
      authServerSessionClient = createAuthServerSessionClient({
        logger,
        authServerUrl: account.authServerUrl,
        daemonToken: account.daemonToken,
      });

      // Cross-host SSH: enroll the daemon's client-identity pubkey under the
      // host owner so sibling daemons' allow-lists resolve it. Fire-and-
      // forget; failure here only disables outbound cross-host dialing — the
      // daemon still serves inbound connections normally. The route is
      // idempotent (upsert), so re-running on every boot is fine.
      void enrollDaemonClientIdentity({
        logger,
        authServerUrl: account.authServerUrl,
        daemonToken: account.daemonToken,
        pubkey: daemonClientIdentity.publicKeyB64,
        serverId,
      });

      // (Cross-host SSH local listener is attached AFTER `httpServer` is
      // created below. Keep this comment as a breadcrumb — the attach call
      // lives near `scriptProxyUpgradeHandler` for ordering reasons.)
      // First refresh must complete BEFORE the WS server begins accepting
      // connections — otherwise the resolver is wired but the pubkey→userId
      // map is empty, and legit clients get 1008-rejected during the window.
      // Subsequent refreshes happen on the long-lived poll loop started by
      // `start()`.
      try {
        await authServerClient.refresh();
      } catch (err) {
        logger.error(
          { err },
          "auth-server first refresh failed — daemon cannot resolve pubkeys; aborting startup",
        );
        throw err;
      }
      authServerClient.start();

      // Stamp ownership onto any legacy null-owner records before the WS
      // server accepts connections. Runs at most once per daemon home —
      // marker file at $APPOSTLE_HOME/migrations/stamp-legacy-owners.done.
      try {
        await stampLegacyOwnerIds({
          appostleHome: config.appostleHome,
          ownerUserId,
          logger,
        });
      } catch (err) {
        logger.error(
          { err },
          "stamp-legacy-owners migration failed — refusing to start to avoid cross-user bleed",
        );
        throw err;
      }

      // One-shot cleanup: remove the legacy file-backed todo store. Todos now
      // live on the auth-server. The file is no longer read — delete it so it
      // doesn't confuse future migrations or disk inspections.
      const legacyTodosPath = path.join(config.appostleHome, "todos.json");
      if (existsSync(legacyTodosPath)) {
        try {
          await unlink(legacyTodosPath);
          logger.info(
            { path: legacyTodosPath },
            "deleting legacy todo store ($APPOSTLE_HOME/todos.json) — todos now live on the auth-server",
          );
        } catch (err) {
          logger.warn(
            { err, path: legacyTodosPath },
            "failed to delete legacy todo store — continuing",
          );
        }
      }
    }

    const staticDir = config.staticDir;
    const downloadTokenTtlMs = config.downloadTokenTtlMs ?? 60000;

    const downloadTokenStore = new DownloadTokenStore({ ttlMs: downloadTokenTtlMs });

    const listenTarget = parseListenString(config.listen);

    const app = express();
    let workspaceRegistry: FileBackedWorkspaceRegistry | null = null;

    const scriptRouteStore = new ScriptRouteStore();
    const scriptRuntimeStore = new WorkspaceScriptRuntimeStore();
    const configuredHostnames = config.hostnames ?? config.allowedHosts;
    let wsServer: VoiceAssistantWebSocketServer | null = null;
    const scriptHealthMonitor = new ScriptHealthMonitor({
      routeStore: scriptRouteStore,
      onChange: createScriptStatusEmitter({
        sessions: () =>
          wsServer?.listActiveSessions().map((session) => ({
            emit: (message) => session.emitServerMessage(message),
          })) ?? [],
        routeStore: scriptRouteStore,
        runtimeStore: scriptRuntimeStore,
        daemonPort: () => (boundListenTarget?.type === "tcp" ? boundListenTarget.port : null),
        resolveWorkspaceDirectory: async (workspaceId) =>
          (await workspaceRegistry?.get(workspaceId))?.cwd ?? null,
      }),
    });
    const handleBranchChange = createBranchChangeRouteHandler({
      routeStore: scriptRouteStore,
      onRoutesChanged: (workspaceId) => {
        scriptHealthMonitor.invalidateWorkspace(workspaceId);
      },
      logger,
    });

    // Host allowlist / DNS rebinding protection (vite-like semantics).
    // For non-TCP (unix sockets), skip host validation.
    if (listenTarget.type === "tcp") {
      app.use((req, res, next) => {
        const hostHeader = typeof req.headers.host === "string" ? req.headers.host : undefined;
        if (!isHostnameAllowed(hostHeader, configuredHostnames)) {
          res.status(403).json({ error: "Invalid Host header" });
          return;
        }
        next();
      });
    }

    // Script proxy — intercepts requests for registered *.localhost hostnames
    // and forwards them to the corresponding local script port. Placed after
    // the host allowlist (*.localhost is already allowed) but before CORS and
    // the rest of the routes so proxied requests skip unnecessary middleware.
    app.use(createScriptProxyMiddleware({ routeStore: scriptRouteStore, logger }));

    // CORS - allow same-origin + configured origins
    const allowedOrigins = new Set([
      ...config.corsAllowedOrigins,
      // Packaged desktop renderers use the custom appostle:// protocol scheme.
      "appostle://app",
      // For TCP, add localhost variants
      ...(listenTarget.type === "tcp"
        ? [
            `http://${listenTarget.host}:${listenTarget.port}`,
            `http://localhost:${listenTarget.port}`,
            `http://127.0.0.1:${listenTarget.port}`,
          ]
        : []),
    ]);

    app.use((req, res, next) => {
      const origin = req.headers.origin;
      if (origin && (allowedOrigins.has("*") || allowedOrigins.has(origin))) {
        res.setHeader("Access-Control-Allow-Origin", origin);
        res.setHeader("Access-Control-Allow-Methods", "GET, POST, DELETE, OPTIONS");
        res.setHeader("Access-Control-Allow-Headers", "Content-Type, Authorization");
        res.setHeader("Access-Control-Allow-Credentials", "true");
      }
      if (req.method === "OPTIONS") {
        res.status(204).end();
        return;
      }
      next();
    });

    // Serve static files from public directory
    app.use("/public", express.static(staticDir));

    // Middleware
    app.use(express.json());

    // Health check endpoint
    app.get("/api/health", (_req, res) => {
      res.json({ status: "ok", timestamp: new Date().toISOString() });
    });

    app.get("/api/status", (_req, res) => {
      res.json({
        status: "server_info",
        serverId,
        hostname: getHostname(),
        version: daemonVersion,
        listen: formatListenTarget(boundListenTarget ?? listenTarget),
      });
    });

    app.get("/api/files/download", async (req, res) => {
      const token =
        typeof req.query.token === "string" && req.query.token.trim().length > 0
          ? req.query.token.trim()
          : null;

      if (!token) {
        res.status(400).json({ error: "Missing download token" });
        return;
      }

      const entry = downloadTokenStore.consumeToken(token);
      if (!entry) {
        res.status(403).json({ error: "Invalid or expired token" });
        return;
      }

      try {
        const fileStats = await stat(entry.absolutePath);
        if (!fileStats.isFile()) {
          res.status(404).json({ error: "File not found" });
          return;
        }

        const safeFileName = entry.fileName.replace(/["\r\n]/g, "_");
        res.setHeader("Content-Type", entry.mimeType);
        res.setHeader("Content-Disposition", `attachment; filename="${safeFileName}"`);
        res.setHeader("Content-Length", entry.size.toString());

        const stream = createReadStream(entry.absolutePath);
        stream.on("error", (err) => {
          logger.error({ err }, "Failed to stream download");
          if (!res.headersSent) {
            res.status(500).json({ error: "Failed to read file" });
          } else {
            res.end();
          }
        });
        stream.pipe(res);
      } catch (err) {
        logger.error({ err }, "Failed to download file");
        if (!res.headersSent) {
          res.status(404).json({ error: "File not found" });
        }
      }
    });

    const httpServer = createHTTPServer(app);

    // Script proxy WebSocket upgrade handler — must be registered before the
    // VoiceAssistantWebSocketServer attaches its own "upgrade" listener so that
    // script-bound upgrades are forwarded first. The handler is a no-op for
    // requests that don't match a registered script route.
    const scriptProxyUpgradeHandler = createScriptProxyUpgradeHandler({
      routeStore: scriptRouteStore,
      logger,
    });
    httpServer.on("upgrade", scriptProxyUpgradeHandler);

    // Cross-host SSH: local loopback-only listener for `appostle ssh-proxy`.
    // Attached here (right after httpServer is created) so it shares the same
    // bound port. The listener self-checks `remoteAddress` for loopback before
    // accepting an upgrade; non-localhost connections are rejected. Only wired
    // in "linked" mode, since it needs the auth-server credentials and the
    // daemon's client-identity keypair.
    let stopLocalTcpForward: (() => void) | null = null;
    if (linkedDaemonAccount) {
      stopLocalTcpForward = attachLocalTcpForwardListener({
        httpServer,
        logger,
        authServerUrl: linkedDaemonAccount.authServerUrl,
        daemonToken: linkedDaemonAccount.daemonToken,
        clientIdentity: daemonClientIdentity.keyPair,
        clientIdentityPubkeyB64: daemonClientIdentity.publicKeyB64,
      }).stop;
    }
    // Keep TS happy if no shutdown path consumes this in the current scope.
    void stopLocalTcpForward;

    const agentStorage = new AgentStorage(config.agentStoragePath, logger);
    const projectRegistry = new FileBackedProjectRegistry(
      path.join(config.appostleHome, "projects", "projects.json"),
      logger,
    );
    workspaceRegistry = new FileBackedWorkspaceRegistry(
      path.join(config.appostleHome, "projects", "workspaces.json"),
      logger,
    );
    const workspaceStacksService = new WorkspaceStacksService({
      filePath: path.join(config.appostleHome, "workspace-stacks.json"),
      logger,
    });
    const chatService = new FileBackedChatService({
      appostleHome: config.appostleHome,
      logger,
    });
    // Fire-and-forget the daemon-wide Claude usage probe. It maintains a
    // singleton interactive `claude` pty whose statusLine relay reports the
    // 5h/7d plan utilization buckets — the only public surface that ships
    // those numbers at all usage levels (the SDK's `rate_limit_event` only
    // includes utilization near a limit). See ./agent/providers/claude/usage-probe.ts.
    void startClaudeUsageProbe({ appostleHome: config.appostleHome, logger });

    const terminalManager = createTerminalManager();
    const github = createGitHubService();
    const workspaceGitService = new WorkspaceGitServiceImpl({
      logger,
      appostleHome: config.appostleHome,
      deps: {
        github,
      },
    });

    // Daemon-wide git-remote resolver and todo proxy. The todo store is a no-op
    // proxy when authServerTodoClient is null (awaiting_link mode — no account,
    // so no todos endpoint available). NoRemoteError is surfaced to the MCP
    // caller so Phase 4 can show a friendly explainer.
    const gitRemoteResolver = new GitRemoteResolver(workspaceGitService);
    // In awaiting_link mode authServerTodoClient is null. We need a TodoStore
    // instance for the Session constructor, so we create a dummy client that
    // always throws. In practice, linked daemons always have the real client.
    const effectiveTodoClient: AuthServerTodoClient = authServerTodoClient ?? {
      listTodos: async () => {
        throw new Error("daemon not linked to auth-server");
      },
      createTodo: async () => {
        throw new Error("daemon not linked to auth-server");
      },
      updateTodo: async () => {
        throw new Error("daemon not linked to auth-server");
      },
      deleteTodo: async () => {
        throw new Error("daemon not linked to auth-server");
      },
      reorderTodos: async () => {
        throw new Error("daemon not linked to auth-server");
      },
      createSubtodo: async () => {
        throw new Error("daemon not linked to auth-server");
      },
      updateSubtodo: async () => {
        throw new Error("daemon not linked to auth-server");
      },
      deleteSubtodo: async () => {
        throw new Error("daemon not linked to auth-server");
      },
      reorderSubtodos: async () => {
        throw new Error("daemon not linked to auth-server");
      },
      listTodoActivity: async () => {
        throw new Error("daemon not linked to auth-server");
      },
      listTodoSessions: async () => {
        throw new Error("daemon not linked to auth-server");
      },
      addTodoComment: async () => {
        throw new Error("daemon not linked to auth-server");
      },
      getDueTodos: async () => {
        throw new Error("daemon not linked to auth-server");
      },
    };
    const todoStore = new TodoStore({
      authServerTodoClient: effectiveTodoClient,
      gitRemoteResolver,
      serverId,
      logger,
    });

    // Resolve an agent's cwd to a workspaceId via the workspace registry,
    // longest-prefix match for worktrees that aren't exact-cwd registered.
    const resolveAgentWorkspace = async (cwd: string): Promise<{ workspaceId: string } | null> => {
      const workspaces = await workspaceRegistry.list();
      const exact = workspaces.find((ws) => ws.cwd === cwd);
      const match =
        exact ??
        workspaces
          .filter((ws) => cwd === ws.cwd || cwd.startsWith(`${ws.cwd}/`))
          .sort((a, b) => b.cwd.length - a.cwd.length)[0];
      return match ? { workspaceId: match.workspaceId } : null;
    };

    // Best-effort markdown → {title, notes, subtasks} parser used when the
    // MCP caller passes raw `markdown` instead of structured fields. First
    // non-blank line is the title; `- [ ]` / `- [x]` lines become subtasks;
    // everything else becomes notes.
    const parseMarkdownTodo = (
      markdown: string,
    ): { title: string; notes: string | undefined; subtasks: string[] } => {
      const lines = markdown.split(/\r?\n/);
      let title = "";
      const subtasks: string[] = [];
      const noteLines: string[] = [];
      let i = 0;
      while (i < lines.length && lines[i].trim() === "") i++;
      if (i < lines.length) {
        title = lines[i]
          .trim()
          .replace(/^#+\s*/, "")
          .slice(0, 200);
        i++;
      }
      for (; i < lines.length; i++) {
        const line = lines[i];
        const m = line.match(/^\s*[-*+]\s*\[\s*[xX ]?\s*\]\s*(.+)$/);
        if (m) {
          subtasks.push(m[1].trim().slice(0, 200));
        } else {
          noteLines.push(line);
        }
      }
      while (noteLines.length > 0 && noteLines[0].trim() === "") noteLines.shift();
      while (noteLines.length > 0 && noteLines[noteLines.length - 1].trim() === "") noteLines.pop();
      return {
        title,
        notes: noteLines.length > 0 ? noteLines.join("\n") : undefined,
        subtasks,
      };
    };

    const addTodoFromAgent = async ({
      cwd,
      agentId,
      title,
      notes,
      subtasks,
      mergeIntoTodoId,
      markdown,
    }: {
      cwd: string;
      agentId: string;
      title?: string;
      notes?: string;
      subtasks?: string[];
      mergeIntoTodoId?: string;
      markdown?: string;
    }): Promise<
      { ok: true; todoId: string; mode: "created" | "merged" } | { ok: false; error: string }
    > => {
      try {
        const workspace = await resolveAgentWorkspace(cwd);
        if (!workspace) {
          return { ok: false, error: `no project registered for cwd ${cwd}` };
        }

        // Reconcile structured inputs with the markdown fallback. Structured
        // fields win; markdown only fills in gaps.
        let effectiveTitle = title?.trim().slice(0, 200) ?? "";
        let effectiveNotes = notes;
        let effectiveSubtasks =
          subtasks?.map((s) => s.trim().slice(0, 200)).filter((s) => s.length > 0) ?? [];
        if (!effectiveTitle && markdown) {
          const parsed = parseMarkdownTodo(markdown);
          effectiveTitle = parsed.title;
          if (effectiveNotes === undefined) effectiveNotes = parsed.notes;
          if (effectiveSubtasks.length === 0) effectiveSubtasks = parsed.subtasks;
        }

        if (mergeIntoTodoId) {
          // Validate the parent lives in this workspace before mutating.
          const { todos } = await todoStore.list(workspace.workspaceId, cwd);
          const parent = todos.find((t) => t.id === mergeIntoTodoId);
          if (!parent) {
            return {
              ok: false,
              error: `merge target todo ${mergeIntoTodoId} not found in this workspace`,
            };
          }
          const toAdd =
            effectiveSubtasks.length > 0
              ? effectiveSubtasks
              : effectiveTitle
                ? [effectiveTitle]
                : [];
          if (toAdd.length === 0) {
            return {
              ok: false,
              error: "merge requested but no subtasks (or title) to append",
            };
          }
          for (const text of toAdd) {
            await todoStore.addSubtodo(parent.id, text);
          }
          wsServer?.broadcastTodoListState(workspace.workspaceId);
          return { ok: true, todoId: parent.id, mode: "merged" };
        }

        if (!effectiveTitle) {
          return {
            ok: false,
            error: "title is required (or pass `markdown` as fallback)",
          };
        }

        const record = await todoStore.add({
          workspaceId: workspace.workspaceId,
          cwd,
          text: effectiveTitle,
          createdByAgentId: agentId,
          createdByCwd: cwd,
          linkedAgentHostId: serverId,
          linkedAgentId: agentId,
        });
        if (effectiveNotes && effectiveNotes.trim().length > 0) {
          await todoStore.updateNotes(record.id, effectiveNotes);
        }
        for (const sub of effectiveSubtasks) {
          await todoStore.addSubtodo(record.id, sub);
        }
        wsServer?.broadcastTodoListState(workspace.workspaceId);
        return { ok: true, todoId: record.id, mode: "created" };
      } catch (error) {
        if (error instanceof NoRemoteError) {
          return { ok: false, error: "no_remote" };
        }
        const message = error instanceof Error ? error.message : String(error);
        return { ok: false, error: message };
      }
    };

    const listTodosForAgent = async ({
      cwd,
    }: {
      cwd: string;
    }): Promise<
      | {
          ok: true;
          todos: Array<{
            id: string;
            title: string;
            subtasks: Array<{ id: string; text: string; completed: boolean }>;
          }>;
        }
      | { ok: false; error: string }
    > => {
      try {
        const workspace = await resolveAgentWorkspace(cwd);
        if (!workspace) {
          return { ok: false, error: `no project registered for cwd ${cwd}` };
        }
        const { todos } = await todoStore.list(workspace.workspaceId, cwd);
        const open = todos
          .filter((t) => t.completedAt === null)
          .map((t) => ({
            id: t.id,
            title: t.text,
            subtasks: (t.subtodos ?? []).map((s) => ({
              id: s.id,
              text: s.text,
              completed: s.completedAt !== null,
            })),
          }));
        return { ok: true, todos: open };
      } catch (error) {
        if (error instanceof NoRemoteError) {
          return { ok: false, error: "no_remote" };
        }
        const message = error instanceof Error ? error.message : String(error);
        return { ok: false, error: message };
      }
    };

    const completeTodoFromAgent = async ({
      cwd,
      todoId,
      report,
      agentId,
    }: {
      cwd: string;
      todoId: string;
      report?: string;
      agentId?: string | null;
    }): Promise<{ ok: true } | { ok: false; error: string }> => {
      try {
        const workspace = await resolveAgentWorkspace(cwd);
        if (!workspace) {
          return { ok: false, error: `no project registered for cwd ${cwd}` };
        }
        // Validate the todo lives in this workspace before mutating, so an
        // agent can only complete its own workspace's todos.
        const { todos } = await todoStore.list(workspace.workspaceId, cwd);
        if (!todos.some((t) => t.id === todoId)) {
          return {
            ok: false,
            error: `todo ${todoId} not found in this workspace`,
          };
        }
        await todoStore.updateStatus(todoId, "done", {
          actorAgentId: agentId ?? null,
          actorHostId: serverId,
        });
        if (report) {
          await todoStore.updateResultReport(todoId, report);
        }
        wsServer?.broadcastTodoListState(workspace.workspaceId);
        return { ok: true };
      } catch (error) {
        if (error instanceof NoRemoteError) {
          return { ok: false, error: "no_remote" };
        }
        const message = error instanceof Error ? error.message : String(error);
        return { ok: false, error: message };
      }
    };

    // Per-user Claude OAuth tokens (bring-your-own-account on shared machines).
    // Single instance shared between the AgentManager (injects the token at
    // spawn) and the WebSocketServer/Session (paste/check/remove handlers).
    const claudeTokenStore = new ClaudeTokenStore(config.appostleHome, logger);

    // Shared by the promote (internal → visible) and schedule-spawn paths:
    // resolve the workspace by cwd, resolve the canonical project key,
    // materialise an own-agent tab on the auth-server (no parent tab →
    // falls back to the owner's Sessions stack), then ping the owner's
    // connected clients so their tab list refetches immediately.
    const materializeOwnAgentTab = linkedDaemonAccount
      ? async (params: {
          agentId: string;
          cwd: string;
          ownerUserId: string;
          title: string | null;
          provider: string;
          reason: "agent-promoted" | "schedule-spawned" | "agent-unarchived";
        }): Promise<void> => {
          const account = linkedDaemonAccount!;
          const { agentId, cwd, ownerUserId: tabOwnerUserId, title, provider, reason } = params;
          if (!workspaceRegistry) {
            logger.warn({ agentId, reason }, "materializeOwnAgentTab: workspaceRegistry not ready");
            return;
          }
          const workspaces = await workspaceRegistry.list();
          const workspace = workspaces.find((w) => w.cwd === cwd) ?? null;
          if (!workspace) {
            logger.warn({ agentId, cwd, reason }, "materializeOwnAgentTab: no workspace for cwd");
            return;
          }
          let dotfileProjectId: string | null;
          try {
            dotfileProjectId = await resolveCanonicalProjectId(workspace.cwd);
          } catch (err) {
            logger.warn(
              { err, agentId, reason },
              "materializeOwnAgentTab: could not resolve canonical project key",
            );
            return;
          }
          if (!dotfileProjectId) {
            logger.warn({ agentId, reason }, "materializeOwnAgentTab: no canonical project key");
            return;
          }
          const result = await createOwnAgentTabInheritingStack({
            authServerUrl: account.authServerUrl,
            daemonToken: account.daemonToken,
            ownerUserId: tabOwnerUserId,
            dotfileProjectId,
            workspaceDisplayName: workspace.displayName,
            workspaceCwd: workspace.cwd,
            childAgentId: agentId,
            childAgentTitle: title,
            // The agent has no parent — pass itself; the helper falls back
            // to the owner's Sessions stack when no parent tab is found.
            parentAgentId: agentId,
            provider,
            logger: logger.child({ component: "own-agent-tab", trigger: reason }),
          });
          if (result.created && result.projectId && result.tabBody) {
            wsServer?.emitMessageToUser(tabOwnerUserId, {
              type: "tab_appeared",
              projectId: result.projectId,
              tab: result.tabBody,
            });
          }
          wsServer?.emitMessageToUser(tabOwnerUserId, {
            type: "tab_sync_invalidate",
            reason,
          });
        }
      : null;

    const agentManager = new AgentManager({
      clients: {
        ...createAllClients(logger, {
          runtimeSettings: config.agentProviderSettings,
          providerOverrides: config.providerOverrides,
        }),
        ...config.agentClients,
      },
      registry: agentStorage,
      claudeTokenStore,
      requireGuestClaudeToken: config.requireGuestClaudeToken ?? false,
      chromeEnabled: config.chromeEnabled ?? true,
      playwrightEnabled: config.playwrightEnabled ?? true,
      // AgentManager treats ownerUserId as undefined-or-string. In
      // awaiting_link mode there is no owner — the manager is built solely
      // because Session's constructor requires one; its mutation surface
      // is never reached because every non-link WS message is rejected.
      ownerUserId: ownerUserId ?? undefined,
      logger,
      appostleRuntimeContextProvider: linkedDaemonAccount
        ? async () => {
            const lookup = createAuthServerLookup({
              logger,
              authServerUrl: linkedDaemonAccount.authServerUrl,
              daemonToken: linkedDaemonAccount.daemonToken,
            });
            const peers = await lookup.listPeers();
            return buildAppostleRuntimeMachineContext(
              peers.map((peer) => ({
                serverId: peer.serverId,
                name: peer.name,
                online: peer.online,
                organization: peer.organization ?? null,
                owner: peer.owner ?? null,
              })),
            );
          }
        : null,
      // Powers the cross-host Recents screen via the auth-server's session
      // projection. Null in awaiting_link mode — the manager treats it as
      // a no-op and skips all fan-out.
      sessionClient: authServerSessionClient,
      // Registries used by the session fan-out to resolve display strings
      // (project name, workspace label) so the projection rows render with
      // human-friendly labels instead of the raw cwd. Available in linked
      // mode; null in awaiting_link mode (which also nulls sessionClient,
      // so the fan-out is a complete no-op there anyway).
      workspaceRegistry,
      // Live share path — when shareSession grants a new recipient,
      // materialise their tab in `Shared with me` on the auth-server
      // immediately. The boot-time backfill handles this idempotently
      // on restart; this hook closes the gap between boots.
      onShareCreated: linkedDaemonAccount
        ? async ({
            agentId,
            agentCwd,
            agentTitle,
            agentOwnerUserId,
            recipientUserId,
            agentArchived,
          }) => {
            const account = linkedDaemonAccount!;
            // Need workspace metadata (cwd → displayName + dotfile id).
            // Registry isn't constructed yet at this exact line, but
            // by the time shareSession ever fires the registry is in
            // place — that's why we resolve it lazily inside the
            // callback.
            if (!workspaceRegistry) {
              logger.warn(
                { agentId, recipientUserId },
                "onShareCreated: workspace registry not ready yet",
              );
              return;
            }
            const workspaces = await workspaceRegistry.list();
            const workspace = workspaces.find((w) => w.cwd === agentCwd) ?? null;
            if (!workspace) {
              logger.warn(
                { agentId, agentCwd, recipientUserId },
                "onShareCreated: no workspace matched agent cwd",
              );
              return;
            }
            let dotfileProjectId: string | null;
            try {
              dotfileProjectId = await resolveCanonicalProjectId(workspace.cwd);
            } catch (err) {
              logger.warn(
                { err, agentId, recipientUserId },
                "onShareCreated: could not resolve canonical project key",
              );
              return;
            }
            if (!dotfileProjectId) {
              logger.warn(
                { agentId, recipientUserId },
                "onShareCreated: no canonical project key for workspace",
              );
              return;
            }
            await createSharedSessionTab({
              authServerUrl: account.authServerUrl,
              daemonToken: account.daemonToken,
              recipientUserId,
              dotfileProjectId,
              workspaceDisplayName: workspace.displayName,
              workspaceCwd: workspace.cwd,
              agentId,
              agentTitle,
              agentOwnerUserId,
              tabState: agentArchived ? "ARCHIVED" : "LIVE",
              logger: logger.child({ component: "shared-session-tab", trigger: "live-share" }),
            });
            // Hydration push — tell the recipient's connected clients to
            // refetch tabs now, instead of waiting up to 30s for the
            // poll cycle.
            wsServer?.emitMessageToUser(recipientUserId, {
              type: "tab_sync_invalidate",
              reason: "share-created",
            });
          }
        : undefined,
      // Archive fan-out — owner-centric model: owner's tab → ARCHIVED
      // (their history), every sharee's tab → DELETED (clean boundary).
      // Triggered by `archiveAgent` after the agent's `archivedAt` flips.
      onAgentArchived: linkedDaemonAccount
        ? async ({ agentId, agentCwd, agentOwnerUserId, sharees }) => {
            const account = linkedDaemonAccount!;
            if (!workspaceRegistry) return;
            const workspaces = await workspaceRegistry.list();
            const workspace = workspaces.find((w) => w.cwd === agentCwd) ?? null;
            if (!workspace) return;
            let dotfileProjectId: string | null;
            try {
              dotfileProjectId = await resolveCanonicalProjectId(workspace.cwd);
            } catch {
              return;
            }
            if (!dotfileProjectId) return;
            await archiveAgentTabsAcrossWatchers({
              authServerUrl: account.authServerUrl,
              daemonToken: account.daemonToken,
              dotfileProjectId,
              agentOwnerUserId,
              sharees,
              agentId,
              logger: logger.child({
                component: "archive-fan-out",
                trigger: "live-archive",
              }),
            });
            // Hydration push for the owner AND every (now-former) sharee.
            // Owner needs to see their Sessions tab move to Archive;
            // sharees need their Shared-with-me tab to vanish.
            wsServer?.emitMessageToUser(agentOwnerUserId, {
              type: "tab_sync_invalidate",
              reason: "agent-archived",
            });
            for (const share of sharees) {
              wsServer?.emitMessageToUser(share.userId, {
                type: "tab_sync_invalidate",
                reason: "agent-archived",
              });
            }
          }
        : undefined,
      // Unarchive fan-out — mirror of onAgentArchived. The owner's tab must
      // flip back to LIVE (the /daemon/sync/tabs upsert revives archived
      // rows), and every sharee — whose tab was DELETED at archive time —
      // gets a fresh "Shared with me" tab. Without this, restoring on one
      // device leaves every other device and watcher stale.
      onAgentUnarchived:
        materializeOwnAgentTab && linkedDaemonAccount
          ? async ({ agentId, agentCwd, agentOwnerUserId, agentTitle, provider, sharees }) => {
              const account = linkedDaemonAccount!;
              await materializeOwnAgentTab({
                agentId,
                cwd: agentCwd,
                ownerUserId: agentOwnerUserId,
                title: agentTitle,
                provider,
                reason: "agent-unarchived",
              });
              if (sharees.length === 0 || !workspaceRegistry) return;
              const workspaces = await workspaceRegistry.list();
              const workspace = workspaces.find((w) => w.cwd === agentCwd) ?? null;
              if (!workspace) return;
              let dotfileProjectId: string | null;
              try {
                dotfileProjectId = await resolveCanonicalProjectId(workspace.cwd);
              } catch {
                return;
              }
              if (!dotfileProjectId) return;
              for (const share of sharees) {
                if (share.userId === agentOwnerUserId) continue;
                await createSharedSessionTab({
                  authServerUrl: account.authServerUrl,
                  daemonToken: account.daemonToken,
                  recipientUserId: share.userId,
                  dotfileProjectId,
                  workspaceDisplayName: workspace.displayName,
                  workspaceCwd: workspace.cwd,
                  agentId,
                  agentTitle,
                  agentOwnerUserId,
                  tabState: "LIVE",
                  logger: logger.child({
                    component: "shared-session-tab",
                    trigger: "unarchive",
                  }),
                });
                wsServer?.emitMessageToUser(share.userId, {
                  type: "tab_sync_invalidate",
                  reason: "agent-unarchived",
                });
              }
            }
          : undefined,
      // Promote fan-out — internal agent flipped visible. Internal agents
      // never get tabs, so materialise one now (Sessions stack fallback)
      // or the promoted agent stays invisible in every tab strip.
      onAgentPromoted: materializeOwnAgentTab
        ? async ({ agentId, agentCwd, agentOwnerUserId, agentTitle, provider }) => {
            await materializeOwnAgentTab({
              agentId,
              cwd: agentCwd,
              ownerUserId: agentOwnerUserId,
              title: agentTitle,
              provider,
              reason: "agent-promoted",
            });
          }
        : undefined,
      // Revoke fan-out — sharee closed their Shared-with-me tab. Owner
      // is untouched; only the revoked sharee's tab gets removed.
      onShareRevoked: linkedDaemonAccount
        ? async ({ agentId, agentCwd, revokedUserId }) => {
            const account = linkedDaemonAccount!;
            if (!workspaceRegistry) return;
            const workspaces = await workspaceRegistry.list();
            const workspace = workspaces.find((w) => w.cwd === agentCwd) ?? null;
            if (!workspace) return;
            let dotfileProjectId: string | null;
            try {
              dotfileProjectId = await resolveCanonicalProjectId(workspace.cwd);
            } catch {
              return;
            }
            if (!dotfileProjectId) return;
            await deleteSharedSessionTab({
              authServerUrl: account.authServerUrl,
              daemonToken: account.daemonToken,
              dotfileProjectId,
              recipientUserId: revokedUserId,
              agentId,
              logger: logger.child({
                component: "shared-session-tab",
                trigger: "live-revoke",
              }),
            });
            // Hydration push so the revoked user's Shared-with-me tab
            // disappears immediately.
            wsServer?.emitMessageToUser(revokedUserId, {
              type: "tab_sync_invalidate",
              reason: "share-revoked",
            });
          }
        : undefined,
    });
    const providerRegistry = buildProviderRegistry(logger, {
      runtimeSettings: config.agentProviderSettings,
      providerOverrides: config.providerOverrides,
    });

    // Shared snapshot manager for the handoff MCP tool — lets agents see
    // which providers and models are available on this host.
    const handoffProviderSnapshotManager = new ProviderSnapshotManager(
      providerRegistry,
      logger.child({ module: "handoff-provider-snapshots" }),
    );

    // In awaiting_link mode we do NOT load agent state, do NOT bootstrap
    // workspace registries, and do NOT start the schedule / loop / quest
    // services. The daemon is a pairing target and nothing else — exposing
    // any of that surface would be a bug (there's no resolver, so we can't
    // enforce per-user visibility, and there's no owner, so "whose agent is
    // this?" has no answer). The objects below are still constructed
    // because the Session/WS-server constructors demand them, but their
    // initializers are deliberately skipped.
    const detachAgentStoragePersistence =
      daemonMode === "linked"
        ? attachAgentStoragePersistence(logger, agentManager, agentStorage)
        : () => {};
    const checkoutDiffManager = new CheckoutDiffManager({
      logger,
      appostleHome: config.appostleHome,
      workspaceGitService,
    });
    const loopService = new LoopService({
      appostleHome: config.appostleHome,
      logger,
      agentManager,
    });
    const questService = new QuestService({
      appostleHome: config.appostleHome,
      logger,
      agentManager,
    });
    const scheduleService = new ScheduleService({
      appostleHome: config.appostleHome,
      logger,
      agentManager,
      agentStorage,
      // Schedule-spawned agents are real user-visible sessions — without a
      // tab they'd be invisible in every stack despite being fully alive
      // on the daemon. Mirrors the handoff/promote materialisation path.
      onAgentSpawned: materializeOwnAgentTab
        ? async ({ agentId, cwd, ownerUserId: spawnOwnerUserId, title, provider }) => {
            await materializeOwnAgentTab({
              agentId,
              cwd,
              ownerUserId: spawnOwnerUserId,
              title,
              provider,
              reason: "schedule-spawned",
            });
          }
        : undefined,
    });

    // Todo scheduler: auto-initiates scheduled todos for this daemon.
    // Only wired when the daemon is linked (has an auth-server account + a
    // real authServerTodoClient) and we know the owner userId.
    const todoSchedulerService =
      linkedDaemonAccount && ownerUserId && authServerTodoClient
        ? new TodoSchedulerService({
            serverId,
            ownerUserId,
            authServerTodoClient,
            agentManager,
            logger,
            onAgentSpawned: materializeOwnAgentTab
              ? async ({ agentId, cwd, ownerUserId: spawnOwnerUserId, title, provider }) => {
                  await materializeOwnAgentTab({
                    agentId,
                    cwd,
                    ownerUserId: spawnOwnerUserId,
                    title,
                    provider,
                    reason: "schedule-spawned",
                  });
                }
              : undefined,
            onTodoMutated: (cwd) => {
              void resolveAgentWorkspace(cwd).then((workspace) => {
                if (workspace) wsServer?.broadcastTodoListState(workspace.workspaceId);
              });
            },
          })
        : null;

    if (daemonMode === "linked") {
      await agentStorage.initialize();
      logger.info({ elapsed: elapsed() }, "Agent storage initialized");
      await bootstrapWorkspaceRegistries({
        appostleHome: config.appostleHome,
        agentStorage,
        projectRegistry,
        workspaceRegistry,
        workspaceGitService,
        logger,
      });
      logger.info({ elapsed: elapsed() }, "Workspace registries bootstrapped");
      await chatService.initialize();
      logger.info({ elapsed: elapsed() }, "Chat service initialized");
      await loopService.initialize();
      logger.info({ elapsed: elapsed() }, "Loop service initialized");
      questService.registerRunner("loop", new LoopRunner({ logger }));
      questService.registerRunner("ralph", new RalphRunner({ logger }));
      questService.registerRunner("run", new LoopRunner({ logger }));
      questService.registerRunner("orchestrate", new OrchestratorRunner({ logger }));
      await questService.initialize();
      logger.info({ elapsed: elapsed() }, "Quest service initialized");
      await scheduleService.start();
      logger.info({ elapsed: elapsed() }, "Schedule service initialized");
      todoSchedulerService?.start();
      logger.info({ elapsed: elapsed() }, "Todo scheduler initialized");
      await workspaceStacksService.initialize();
      logger.info({ elapsed: elapsed() }, "Workspace stacks service initialized");
      logger.info({ elapsed: elapsed() }, "Loading persisted agent registry");
      const persistedRecords = await agentStorage.list();

      // Phase 2: tab backfill — idempotent on every boot.
      // Fire-and-forget: a failure here must never block daemon startup.
      if (linkedDaemonAccount && ownerUserId) {
        const backfillAuthServerUrl = linkedDaemonAccount.authServerUrl;
        const backfillDaemonToken = linkedDaemonAccount.daemonToken;
        const backfillOwnerUserId = ownerUserId;
        const backfillStacksFilePath = path.join(config.appostleHome, "workspace-stacks.json");

        void runTabBackfill({
          authServerUrl: backfillAuthServerUrl,
          daemonToken: backfillDaemonToken,
          hostId: linkedDaemonAccount.hostId,
          ownerUserId: backfillOwnerUserId,
          workspaceRegistry,
          agentStorage,
          workspaceStacksFilePath: backfillStacksFilePath,
          projectRegistry,
          logger,
        }).catch((err: unknown) => {
          logger.error({ err }, "tab backfill failed — daemon continues without backfill");
        });

        // Lazy per-workspace backfill: workspaces registered after daemon boot
        // (e.g. opening a brand-new project) must also get a Project / Sessions /
        // Quests / Shared-with-me record on the auth-server. Fire-and-forget so
        // registration succeeds even if the auth-server is unreachable.
        workspaceRegistry.onUpsert((record) => {
          void runWorkspaceBackfill({
            authServerUrl: backfillAuthServerUrl,
            daemonToken: backfillDaemonToken,
            ownerUserId: backfillOwnerUserId,
            workspace: record,
            agentStorage,
            workspaceStacksFilePath: backfillStacksFilePath,
            projectRegistry,
            logger: logger.child({ component: "tab-backfill", trigger: "lazy" }),
          }).catch((err: unknown) => {
            logger.warn(
              { err, workspaceId: record.workspaceId },
              "lazy workspace backfill failed — workspace will be retried on next daemon boot",
            );
          });
        });
      }
      logger.info(
        { elapsed: elapsed() },
        `Agent registry loaded (${persistedRecords.length} record${persistedRecords.length === 1 ? "" : "s"}); agents will initialize on demand`,
      );
    } else {
      logger.info(
        "awaiting_link mode — skipping agent storage init, workspace bootstrap, schedule/loop/quest services",
      );
    }
    logger.info(
      "Voice mode configured for agent-scoped resume flow (no dedicated voice assistant provider)",
    );
    logger.info({ elapsed: elapsed() }, "Preparing voice and MCP runtime");
    const mcpEnabled = config.mcpEnabled ?? true;
    if (mcpEnabled) {
      const agentMcpRoute = "/mcp/agents";
      const agentMcpTransports: AgentMcpTransportMap = new Map();

      const createAgentMcpTransport = async (callerAgentId?: string) => {
        const agentMcpServer = await createAgentMcpServer({
          agentManager,
          agentStorage,
          terminalManager,
          getDaemonTcpPort: () =>
            boundListenTarget?.type === "tcp" ? boundListenTarget.port : null,
          scheduleService,
          providerRegistry,
          github,
          createAppostleWorktree: (input, serviceOptions) => {
            const coreDeps = createWorktreeCoreDeps(github);
            return createAppostleWorktree(input, {
              ...coreDeps,
              ...(serviceOptions?.resolveRepositoryDefaultBranch
                ? {
                    resolveRepositoryDefaultBranch: serviceOptions.resolveRepositoryDefaultBranch,
                  }
                : {}),
              projectRegistry,
              workspaceRegistry,
              workspaceGitService,
              primeWorkspaceGitWatchFingerprints: async (workspace) => {
                await Promise.all(
                  wsServer
                    ?.listActiveSessions()
                    .map((session) =>
                      session.primeWorkspaceGitWatchFingerprintForWorkspace(workspace),
                    ) ?? [],
                );
              },
              broadcastWorkspaceUpdate: async (workspaceId) => {
                await Promise.all(
                  wsServer
                    ?.listActiveSessions()
                    .map((session) => session.emitWorkspaceUpdateForWorkspaceId(workspaceId)) ?? [],
                );
              },
            });
          },
          appostleHome: config.appostleHome,
          callerAgentId,
          enableVoiceTools: false,
          resolveSpeakHandler: (agentId) => wsServer?.resolveVoiceSpeakHandler(agentId) ?? null,
          resolveCallerContext: (agentId) => wsServer?.resolveVoiceCallerContext(agentId) ?? null,
          logger,
        });

        const transport = new StreamableHTTPServerTransport({
          sessionIdGenerator: () => randomUUID(),
          onsessioninitialized: (sessionId) => {
            agentMcpTransports.set(sessionId, transport);
            logger.debug({ sessionId }, "Agent MCP session initialized");
          },
          onsessionclosed: (sessionId) => {
            agentMcpTransports.delete(sessionId);
            logger.debug({ sessionId }, "Agent MCP session closed");
          },
          // NOTE: We enforce a Vite-like host allowlist at the app/websocket layer.
          // StreamableHTTPServerTransport's built-in check requires exact Host header matches.
          enableDnsRebindingProtection: false,
        });

        transport.onclose = () => {
          if (transport.sessionId) {
            agentMcpTransports.delete(transport.sessionId);
          }
        };
        transport.onerror = (err) => {
          logger.error({ err }, "Agent MCP transport error");
        };

        await agentMcpServer.connect(transport);
        return transport;
      };

      const handleAgentMcpRequest: express.RequestHandler = async (req, res) => {
        if (config.mcpDebug) {
          logger.debug(
            {
              method: req.method,
              url: req.originalUrl,
              sessionId: req.header("mcp-session-id"),
              authorization: req.header("authorization"),
              body: req.body,
            },
            "Agent MCP request",
          );
        }
        try {
          const sessionId = req.header("mcp-session-id");
          let transport = sessionId ? agentMcpTransports.get(sessionId) : undefined;

          if (!transport) {
            if (req.method !== "POST") {
              res.status(400).json({
                jsonrpc: "2.0",
                error: {
                  code: -32000,
                  message: "Missing or invalid MCP session",
                },
                id: null,
              });
              return;
            }
            if (!isInitializeRequest(req.body)) {
              res.status(400).json({
                jsonrpc: "2.0",
                error: {
                  code: -32000,
                  message: "Initialization request expected",
                },
                id: null,
              });
              return;
            }
            const callerAgentIdRaw = req.query.callerAgentId;
            const callerAgentId =
              typeof callerAgentIdRaw === "string"
                ? callerAgentIdRaw
                : Array.isArray(callerAgentIdRaw) && typeof callerAgentIdRaw[0] === "string"
                  ? callerAgentIdRaw[0]
                  : undefined;
            transport = await createAgentMcpTransport(callerAgentId);
          }

          await transport.handleRequest(req as any, res as any, req.body);
        } catch (err) {
          logger.error({ err }, "Failed to handle Agent MCP request");
          if (!res.headersSent) {
            res.status(500).json({
              jsonrpc: "2.0",
              error: {
                code: -32603,
                message: "Internal MCP server error",
              },
              id: null,
            });
          }
        }
      };

      app.post(agentMcpRoute, handleAgentMcpRequest);
      app.get(agentMcpRoute, handleAgentMcpRequest);
      app.delete(agentMcpRoute, handleAgentMcpRequest);
      logger.info({ route: agentMcpRoute }, "Agent MCP server mounted on main app");

      // ── /mcp/appostle — Appostle harness toolbelt (cross-provider) ───────
      // Serves Appostle's first-party MCP tools to every agent provider over
      // HTTP. The old in-process SDK path only worked for Claude; this
      // endpoint works for Claude, OpenCode, and Codex alike.
      const appostleMcpRoute = "/mcp/appostle";
      type AppostleMcpTransportMap = Map<string, StreamableHTTPServerTransport>;
      const appostleMcpTransports: AppostleMcpTransportMap = new Map();

      const createAppostleMcpTransport = async (callerAgentId?: string) => {
        // Resolve reachable sibling hosts for `cross_hosts_terminal`. Only when
        // the daemon is linked (needs auth-server creds + client identity). A
        // failed/empty lookup simply means the tool isn't offered this session.
        let crossHostExec: CrossHostExecOptions | null = null;
        if (linkedDaemonAccount) {
          try {
            const lookup = createAuthServerLookup({
              logger,
              authServerUrl: linkedDaemonAccount.authServerUrl,
              daemonToken: linkedDaemonAccount.daemonToken,
            });
            const peers = await lookup.listPeers();
            const reachableSiblings = peers
              .filter((p) => p.online)
              .map((p) => ({
                serverId: p.serverId,
                name: p.name,
                icon: p.icon ?? null,
                directEndpoints: p.directEndpoints ?? [],
                organization: p.organization ?? null,
                owner: p.owner ?? null,
              }));
            if (reachableSiblings.length > 0) {
              crossHostExec = {
                authServerUrl: linkedDaemonAccount.authServerUrl,
                daemonToken: linkedDaemonAccount.daemonToken,
                clientIdentity: daemonClientIdentity.keyPair,
                clientIdentityPubkeyB64: daemonClientIdentity.publicKeyB64,
                reachableSiblings,
              };
            }
          } catch (err) {
            logger.warn({ err }, "cross_hosts_terminal: peer discovery failed; tool not offered");
          }
        }

        const handoffServer = createAppostleHttpMcpServer(callerAgentId ?? "", {
          agentManager,
          agentStorage,
          appostleHome: config.appostleHome,
          logger,
          providerSnapshotManager: handoffProviderSnapshotManager,
          addTodoFromAgent: todoStore ? addTodoFromAgent : null,
          listTodosForAgent: todoStore ? listTodosForAgent : null,
          completeTodoFromAgent: todoStore ? completeTodoFromAgent : null,
          crossHostExec,
          // Eager tab materialisation for handed-off disciples. The child
          // tab lands in the parent's stack on the auth-server immediately,
          // and we ping the owner's other clients so their tab list refetches
          // instead of waiting up to 30s for the next poll cycle.
          onHandoffSpawned: linkedDaemonAccount
            ? async ({
                parentAgentId,
                parentAgentCwd,
                parentAgentOwnerUserId,
                childAgentId,
                childAgentTitle,
                sharees,
              }) => {
                const account = linkedDaemonAccount!;
                logger.info(
                  { parentAgentId, childAgentId, parentAgentOwnerUserId, sharees },
                  "onHandoffSpawned: fired",
                );
                if (!workspaceRegistry) {
                  logger.warn(
                    { parentAgentId, childAgentId },
                    "onHandoffSpawned: workspaceRegistry not ready",
                  );
                  return;
                }
                const workspaces = await workspaceRegistry.list();
                const workspace = workspaces.find((w) => w.cwd === parentAgentCwd) ?? null;
                if (!workspace) {
                  logger.warn(
                    { parentAgentId, parentAgentCwd, childAgentId },
                    "onHandoffSpawned: no workspace matched parent cwd",
                  );
                  return;
                }
                let dotfileProjectId: string | null;
                try {
                  dotfileProjectId = await resolveCanonicalProjectId(workspace.cwd);
                } catch (err) {
                  logger.warn(
                    { err, parentAgentId, childAgentId },
                    "onHandoffSpawned: could not resolve canonical project key",
                  );
                  return;
                }
                if (!dotfileProjectId) {
                  logger.warn(
                    { parentAgentId, childAgentId },
                    "onHandoffSpawned: no canonical project key for workspace",
                  );
                  return;
                }
                const result = await createOwnAgentTabInheritingStack({
                  authServerUrl: account.authServerUrl,
                  daemonToken: account.daemonToken,
                  ownerUserId: parentAgentOwnerUserId,
                  dotfileProjectId,
                  workspaceDisplayName: workspace.displayName,
                  workspaceCwd: workspace.cwd,
                  childAgentId,
                  childAgentTitle,
                  parentAgentId,
                  logger: logger.child({
                    component: "own-agent-tab",
                    trigger: "handoff",
                  }),
                });
                // Push the new tab to the owner's clients INLINE. Beats
                // `tab_sync_invalidate` because the client applies the tab
                // directly — no /me/projects/.../tabs refetch round-trip,
                // no race against in-flight syncAlls. The hydration
                // invalidate stays as a safety net in case `tab_appeared`
                // is dropped or arrives at an old client.
                if (result.created && result.projectId && result.tabBody) {
                  wsServer?.emitMessageToUser(parentAgentOwnerUserId, {
                    type: "tab_appeared",
                    projectId: result.projectId,
                    tab: result.tabBody,
                  });
                }
                wsServer?.emitMessageToUser(parentAgentOwnerUserId, {
                  type: "tab_sync_invalidate",
                  reason: "handoff-spawned",
                });
                // The child inherits the parent's shares, so every sharee
                // must get a "Shared with me" tab too — without this fan-out
                // watchers on other accounts never see the disciple appear.
                for (const share of sharees) {
                  if (share.userId === parentAgentOwnerUserId) continue;
                  await createSharedSessionTab({
                    authServerUrl: account.authServerUrl,
                    daemonToken: account.daemonToken,
                    recipientUserId: share.userId,
                    dotfileProjectId,
                    workspaceDisplayName: workspace.displayName,
                    workspaceCwd: workspace.cwd,
                    agentId: childAgentId,
                    agentTitle: childAgentTitle,
                    agentOwnerUserId: parentAgentOwnerUserId,
                    tabState: "LIVE",
                    logger: logger.child({
                      component: "shared-session-tab",
                      trigger: "handoff",
                    }),
                  });
                  wsServer?.emitMessageToUser(share.userId, {
                    type: "tab_sync_invalidate",
                    reason: "handoff-spawned",
                  });
                }
              }
            : undefined,
        });

        const transport = new StreamableHTTPServerTransport({
          sessionIdGenerator: () => randomUUID(),
          onsessioninitialized: (sessionId) => {
            appostleMcpTransports.set(sessionId, transport);
            logger.debug({ sessionId }, "Appostle MCP session initialized");
          },
          onsessionclosed: (sessionId) => {
            appostleMcpTransports.delete(sessionId);
            logger.debug({ sessionId }, "Appostle MCP session closed");
          },
          enableDnsRebindingProtection: false,
        });

        transport.onclose = () => {
          if (transport.sessionId) {
            appostleMcpTransports.delete(transport.sessionId);
          }
        };
        transport.onerror = (err) => {
          logger.error({ err }, "Appostle MCP transport error");
        };

        await handoffServer.connect(transport);
        return transport;
      };

      const handleAppostleMcpRequest: express.RequestHandler = async (req, res) => {
        if (config.mcpDebug) {
          logger.debug(
            {
              method: req.method,
              url: req.originalUrl,
              sessionId: req.header("mcp-session-id"),
            },
            "Appostle MCP request",
          );
        }
        try {
          const sessionId = req.header("mcp-session-id");
          let transport = sessionId ? appostleMcpTransports.get(sessionId) : undefined;

          if (!transport) {
            if (req.method !== "POST") {
              res.status(400).json({
                jsonrpc: "2.0",
                error: { code: -32000, message: "Missing or invalid MCP session" },
                id: null,
              });
              return;
            }
            if (!isInitializeRequest(req.body)) {
              res.status(400).json({
                jsonrpc: "2.0",
                error: { code: -32000, message: "Initialization request expected" },
                id: null,
              });
              return;
            }
            const callerAgentIdRaw = req.query.callerAgentId;
            const callerAgentId =
              typeof callerAgentIdRaw === "string"
                ? callerAgentIdRaw
                : Array.isArray(callerAgentIdRaw) && typeof callerAgentIdRaw[0] === "string"
                  ? callerAgentIdRaw[0]
                  : undefined;
            transport = await createAppostleMcpTransport(callerAgentId);
          }

          await transport.handleRequest(req as any, res as any, req.body);
        } catch (err) {
          logger.error({ err }, "Failed to handle Appostle MCP request");
          if (!res.headersSent) {
            res.status(500).json({
              jsonrpc: "2.0",
              error: { code: -32603, message: "Internal MCP server error" },
              id: null,
            });
          }
        }
      };

      app.post(appostleMcpRoute, handleAppostleMcpRequest);
      app.get(appostleMcpRoute, handleAppostleMcpRequest);
      app.delete(appostleMcpRoute, handleAppostleMcpRequest);
      logger.info({ route: appostleMcpRoute }, "Appostle handoff MCP server mounted on main app");
    } else {
      logger.info("Agent MCP HTTP endpoint disabled");
    }

    const speechService = createSpeechService({
      logger,
      openaiConfig: config.openai,
      speechConfig: config.speech,
    });
    logger.info({ elapsed: elapsed() }, "Speech service created");

    logger.info({ elapsed: elapsed() }, "Bootstrap complete, ready to start listening");

    const start = async () => {
      // Start main HTTP server
      await new Promise<void>((resolve, reject) => {
        const onError = (err: Error) => {
          httpServer.off("listening", onListening);
          reject(err);
        };
        const onListening = () => {
          httpServer.off("error", onError);
          const logAndResolve = async () => {
            boundListenTarget = resolveBoundListenTarget(listenTarget, httpServer);
            const mcpBaseUrl = mcpEnabled ? createAgentMcpBaseUrl(boundListenTarget) : null;
            const appostleMcpBaseUrl = mcpEnabled
              ? createAppostleMcpBaseUrl(boundListenTarget)
              : null;
            agentManager.setAppostleMcpBaseUrl(
              config.mcpInjectIntoAgents === false ? null : appostleMcpBaseUrl,
            );
            daemonConfigStore.onFieldChange("mcp.injectIntoAgents", (value) => {
              agentManager.setAppostleMcpBaseUrl(value ? appostleMcpBaseUrl : null);
            });
            daemonConfigStore.onFieldChange("chrome.enabled", (value) => {
              agentManager.chromeEnabled = (value as boolean | undefined) ?? true;
            });
            daemonConfigStore.onFieldChange("playwright.enabled", (value) => {
              agentManager.playwrightEnabled = (value as boolean | undefined) ?? true;
            });
            const relayEnabled = config.relayEnabled ?? true;
            const relayEndpoint = config.relayEndpoint ?? "pair.appostle.app:443";
            const relayPublicEndpoint = config.relayPublicEndpoint ?? relayEndpoint;
            const appBaseUrl = config.appBaseUrl ?? "https://appostle.app";

            if (boundListenTarget.type === "tcp") {
              logger.info(
                {
                  host: boundListenTarget.host,
                  port: boundListenTarget.port,
                  elapsed: elapsed(),
                },
                `Server listening on http://${boundListenTarget.host}:${boundListenTarget.port}`,
              );
            } else {
              logger.info(
                { path: boundListenTarget.path, elapsed: elapsed() },
                `Server listening on ${boundListenTarget.path}`,
              );
            }

            wsServer = new VoiceAssistantWebSocketServer(
              httpServer,
              logger,
              serverId,
              agentManager,
              agentStorage,
              downloadTokenStore,
              config.appostleHome,
              daemonConfigStore,
              mcpBaseUrl,
              { allowedOrigins, hostnames: configuredHostnames },
              speechService,
              terminalManager,
              {
                finalTimeoutMs: config.dictationFinalTimeoutMs,
              },
              config.agentProviderSettings,
              config.providerOverrides,
              daemonVersion,
              (intent) => {
                try {
                  config.onLifecycleIntent?.(intent);
                } catch (error) {
                  logger.error({ err: error, intent }, "Failed to handle daemon lifecycle intent");
                }
              },
              projectRegistry,
              workspaceRegistry,
              chatService,
              loopService,
              questService,
              scheduleService,
              checkoutDiffManager,
              scriptRouteStore,
              scriptRuntimeStore,
              handleBranchChange,
              () => (boundListenTarget?.type === "tcp" ? boundListenTarget.port : null),
              () => (boundListenTarget?.type === "tcp" ? boundListenTarget.host : null),
              (hostname) => scriptHealthMonitor.getHealthForHostname(hostname),
              workspaceGitService,
              github,
              undefined,
              workspaceStacksService,
              todoStore,
              claudeTokenStore,
              // Workspace removed/archived on the daemon → delete the project
              // (and its stacks + tabs, all users) on the auth-server so it
              // doesn't linger in recents / tab strips. Best-effort.
              linkedDaemonAccount
                ? (workspace) => {
                    const account = linkedDaemonAccount!;
                    void (async () => {
                      const removalLogger = logger.child({ component: "project-removal" });
                      const projectKey =
                        (await resolveCanonicalProjectId(workspace.cwd).catch(() => null)) ??
                        workspace.projectId;
                      const removed = await deleteProjectOnAuthServer({
                        authServerUrl: account.authServerUrl,
                        daemonToken: account.daemonToken,
                        projectKey,
                        cwd: workspace.cwd,
                        logger: removalLogger,
                      });
                      if (removed && ownerUserId) {
                        wsServer?.emitMessageToUser(ownerUserId, {
                          type: "tab_sync_invalidate",
                          reason: "project-removed",
                        });
                      }
                    })();
                  }
                : undefined,
              // Workspace unarchived (folder re-added) → the removal fan-out
              // deleted the cloud project, so re-backfill it and ping the
              // owner's clients. Best-effort, fire-and-forget.
              linkedDaemonAccount && ownerUserId
                ? (workspace) => {
                    const account = linkedDaemonAccount!;
                    const unarchiveOwnerUserId = ownerUserId!;
                    void (async () => {
                      const unarchiveLogger = logger.child({
                        component: "tab-backfill",
                        trigger: "workspace-unarchived",
                      });
                      await runWorkspaceBackfill({
                        authServerUrl: account.authServerUrl,
                        daemonToken: account.daemonToken,
                        ownerUserId: unarchiveOwnerUserId,
                        workspace,
                        agentStorage,
                        workspaceStacksFilePath: path.join(
                          config.appostleHome,
                          "workspace-stacks.json",
                        ),
                        projectRegistry,
                        logger: unarchiveLogger,
                      });
                      wsServer?.emitMessageToUser(unarchiveOwnerUserId, {
                        type: "tab_sync_invalidate",
                        reason: "workspace-unarchived",
                      });
                    })().catch((err: unknown) => {
                      logger.warn(
                        { err, workspaceId: workspace.workspaceId },
                        "workspace-unarchive backfill failed",
                      );
                    });
                  }
                : undefined,
            );

            // Capture into a non-null local so the closures below don't
            // re-widen `wsServer` back to `VoiceAssistantWebSocketServer
            // | null` (TypeScript can't prove a captured `let` stays
            // non-null across an async deferred invocation).
            const ws = wsServer;

            if (daemonMode === "linked") {
              // Wire the auth-server's pubkey→userId lookup into the ws-server
              // so every new Session pins `ownerUserId` at connect time.
              // `authServerClient` is guaranteed non-null in the "linked"
              // branch and its allow-list has already been populated by the
              // awaited first refresh above.
              ws.setServerPubkeyHex(linkedServerPubkeyHex!);
              // Lenses piece 3: wire scoped-allowlist lookup for client-scoped
              // JWTs. When the auth-server rotates the client's grants (add /
              // revoke), the poll picks it up and the lookup reflects it.
              ws.setScopedLookup((pubkey) => authServerClient!.getScopedForPubkey(pubkey));

              // Local CLI trust: per-boot random token in $APPOSTLE_HOME/
              // cli-token (0600). The CLI presents it as its wsToken over
              // loopback; the ws-server maps it to the daemon owner. Best-
              // effort — a write failure only disables local CLI access.
              try {
                const cliToken = randomBytes(32).toString("hex");
                const cliTokenPath = path.join(config.appostleHome, "cli-token");
                writeFileSync(cliTokenPath, cliToken, { mode: 0o600 });
                ws.setCliToken(cliToken, ownerUserId!);
              } catch (err) {
                logger.warn({ err }, "failed to write cli-token — local CLI access disabled");
              }

              // Runtime relink (`link_account_request`) is still wired so a
              // user who replaces their auth-server account credentials
              // without restarting the daemon can transition cleanly.
              // Refresh the pubkey→userId map BEFORE swapping the resolver
              // so reconnects don't race on an empty allow-list. See gitlab
              // issue #24.
              ws.setOnAccountLinked(async (account) => {
                logger.info(
                  { hostId: account.hostId, userId: account.userId },
                  "onAccountLinked: rotating auth-server client",
                );
                const next = createAuthServerClient({
                  logger,
                  authServerUrl: account.authServerUrl,
                  daemonToken: account.daemonToken,
                  serverPubkeyHex: account.serverPubkey,
                  directEndpoints: () =>
                    boundListenTarget?.type === "tcp"
                      ? buildDirectEndpointCandidates({
                          listenHost: boundListenTarget.host,
                          port: boundListenTarget.port,
                        })
                      : [],
                  onUserRemoved: (userId, reason) => {
                    ws.closeSessionsForUser(userId, `auth-allowlist:${reason}`);
                  },
                });
                next.start();
                await next.refresh();
                authServerClient?.stop();
                authServerClient = next;
                ownerUserId = account.userId;
                ws.setServerPubkeyHex(account.serverPubkey);
                // Re-wire scoped lookup against the new auth-server client.
                ws.setScopedLookup((pubkey) => next.getScopedForPubkey(pubkey));
                // 1012 = Service Restart (RFC 6455). Clients auto-reconnect
                // and hit the resolver against the new allow-list.
                ws.closeAllSessions(1012, "daemon account rotated, reconnect");
                logger.info({ hostId: account.hostId }, "onAccountLinked: complete");
              });
            } else {
              // awaiting_link boot path. There is no auth-server client
              // and no pubkey resolver — sessions are accepted with the
              // sentinel ownerUserId "__awaiting_link__" so the relay can
              // carry the `link_account_request` through to the session.
              ws.setAwaitingLink(true);
              ws.setOnAccountLinked(async (account) => {
                logger.info(
                  { hostId: account.hostId, userId: account.userId },
                  "onAccountLinked (awaiting_link): account block persisted, exiting for systemd restart",
                );
                // Hand control back to the supervisor. The installer's
                // systemd unit ships with `Restart=always` (Fix 2,
                // installer/src/daemon-setup.ts) precisely so a clean
                // `exit(0)` triggers an immediate respawn — the second
                // boot reads the now-populated `account` block and takes
                // the "linked" code path. Brief delay so the
                // `link_account_response` flushes to the client first.
                setTimeout(() => {
                  process.exit(0);
                }, 250);
              });
            }

            if (typeof process.send === "function" && process.env.APPOSTLE_SUPERVISED === "1") {
              process.send({
                type: "appostle:ready",
                listen:
                  boundListenTarget.type === "tcp"
                    ? `${boundListenTarget.host}:${boundListenTarget.port}`
                    : boundListenTarget.path,
              });
            }

            if (relayEnabled) {
              const offer = await createConnectionOfferV2({
                serverId,
                daemonPublicKeyB64: daemonKeyPair.publicKeyB64,
                relay: { endpoint: relayPublicEndpoint },
              });

              encodeOfferToFragmentUrl({ offer, appBaseUrl });

              relayTransport?.stop().catch(() => undefined);
              relayTransport = startRelayTransport({
                logger,
                attachSocket: (ws, metadata) => {
                  if (!wsServer) {
                    throw new Error("WebSocket server not initialized");
                  }
                  return wsServer.attachExternalSocket(ws, metadata);
                },
                relayEndpoint,
                serverId,
                daemonKeyPair: daemonKeyPair.keyPair,
              });
            }
          };

          logAndResolve().then(resolve, reject);
        };
        httpServer.once("error", onError);
        httpServer.once("listening", onListening);

        if (listenTarget.type === "tcp") {
          httpServer.listen(listenTarget.port, listenTarget.host);
        } else {
          if (listenTarget.type === "socket" && existsSync(listenTarget.path)) {
            unlinkSync(listenTarget.path);
          }
          httpServer.listen(listenTarget.path);
        }
      });

      // Start speech service after listening so synchronous Sherpa native
      // model loading doesn't block the server from accepting connections.
      speechService.start();
      scriptHealthMonitor.start();
    };

    const stop = async () => {
      scriptHealthMonitor.stop();
      await closeAllAgents(logger, agentManager);
      await agentManager.flush().catch(() => undefined);
      detachAgentStoragePersistence();
      await agentStorage.flush().catch(() => undefined);
      await shutdownProviders(logger, {
        runtimeSettings: config.agentProviderSettings,
        providerOverrides: config.providerOverrides,
      });
      terminalManager.killAll();
      await stopClaudeUsageProbe().catch(() => undefined);
      speechService.stop();
      await scheduleService.stop().catch(() => undefined);
      todoSchedulerService?.stop();
      await relayTransport?.stop().catch(() => undefined);
      authServerClient?.stop();
      if (wsServer) {
        await wsServer.close();
      }
      await new Promise<void>((resolve) => {
        httpServer.close(() => resolve());
      });
      // Clean up socket files
      if (listenTarget.type === "socket" && existsSync(listenTarget.path)) {
        unlinkSync(listenTarget.path);
      }
    };

    return {
      config,
      agentManager,
      agentStorage,
      terminalManager,
      scriptRouteStore,
      scriptRuntimeStore,
      start,
      stop,
      getListenTarget: () => boundListenTarget,
    };
  } catch (err) {
    throw err;
  }
}

async function closeAllAgents(logger: Logger, agentManager: AgentManager): Promise<void> {
  const agents = agentManager.listAgents();
  for (const agent of agents) {
    try {
      await agentManager.closeAgent(agent.id);
    } catch (err) {
      logger.error({ err, agentId: agent.id }, "Failed to close agent");
    }
  }
}
